import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { callAPIAdmin } from '../../utils/apiUtils';
import { apiUrls } from '../../utils/apiUrls';

export const addVehicleMake = createAsyncThunk(
  'vehicleMake/add',
  async (vehicleMakeData, { rejectWithValue }) => {
    try {
      const response = await callAPIAdmin(apiUrls.adminAddVehicleBrand, {}, 'POST', vehicleMakeData);
      if (response?.data?.status) {
        return response.data.data;
      }
      return rejectWithValue(response?.data?.message || 'Failed to add vehicle make');
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message);
    }
  }
);

export const updateVehicleMake = createAsyncThunk(
  'vehicleMake/update',
  async ({ id, vehicleMakeData }, { rejectWithValue }) => {
    try {
      const response = await callAPIAdmin(apiUrls.adminUpdateVehicleBrand, {}, 'PATCH', { _id: id, ...vehicleMakeData });
      if (response?.data?.status) {
        return response.data.data;
      }
      return rejectWithValue(response?.data?.message || 'Failed to update vehicle make');
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message);
    }
  }
);

export const deleteVehicleMake = createAsyncThunk(
  'vehicleMake/delete',
  async (id, { rejectWithValue }) => {
    try {
      const response = await callAPIAdmin(apiUrls.adminDeleteVehicleBrand, { _id: id }, 'DELETE', {});
      if (response?.data?.status) {
        return { id, message: response.data.message };
      }
      return rejectWithValue(response?.data?.message || 'Failed to delete vehicle make');
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message);
    }
  }
);

export const updateVehicleMakeStatus = createAsyncThunk(
  'vehicleMake/updateStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await callAPIAdmin(apiUrls.adminUpdateStatusVehicleBrand, { _id: id, status }, 'PATCH', {});
      if (response?.data?.status) {
        return { id, status, message: response.data.message };
      }
      return rejectWithValue(response?.data?.message || 'Failed to update status');
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message);
    }
  }
);

const initialState = {
  mutationLoading: false,
  mutationError: null,
  mutationSuccess: null,
};

const adminVehicleMakeSlice = createSlice({
  name: 'vehicleMake',
  initialState,
  reducers: {
    clearMutationError: (state) => {
      state.mutationError = null;
    },
    clearMutationSuccess: (state) => {
      state.mutationSuccess = null;
    },
    resetMutationState: (state) => {
      state.mutationLoading = false;
      state.mutationError = null;
      state.mutationSuccess = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addVehicleMake.pending, (state) => {
        state.mutationLoading = true;
        state.mutationError = null;
        state.mutationSuccess = null;
      })
      .addCase(addVehicleMake.fulfilled, (state) => {
        state.mutationLoading = false;
        state.mutationSuccess = 'Vehicle make added successfully';
      })
      .addCase(addVehicleMake.rejected, (state, action) => {
        state.mutationLoading = false;
        state.mutationError = action.payload;
      })
      .addCase(updateVehicleMake.pending, (state) => {
        state.mutationLoading = true;
        state.mutationError = null;
        state.mutationSuccess = null;
      })
      .addCase(updateVehicleMake.fulfilled, (state) => {
        state.mutationLoading = false;
        state.mutationSuccess = 'Vehicle make updated successfully';
      })
      .addCase(updateVehicleMake.rejected, (state, action) => {
        state.mutationLoading = false;
        state.mutationError = action.payload;
      })
      .addCase(deleteVehicleMake.pending, (state) => {
        state.mutationLoading = true;
        state.mutationError = null;
        state.mutationSuccess = null;
      })
      .addCase(deleteVehicleMake.fulfilled, (state) => {
        state.mutationLoading = false;
        state.mutationSuccess = 'Vehicle make deleted successfully';
      })
      .addCase(deleteVehicleMake.rejected, (state, action) => {
        state.mutationLoading = false;
        state.mutationError = action.payload;
      })
      .addCase(updateVehicleMakeStatus.pending, (state) => {
        state.mutationLoading = true;
        state.mutationError = null;
        state.mutationSuccess = null;
      })
      .addCase(updateVehicleMakeStatus.fulfilled, (state, action) => {
        state.mutationLoading = false;
        state.mutationSuccess = `Status changed to ${action.payload?.status}`;
      })
      .addCase(updateVehicleMakeStatus.rejected, (state, action) => {
        state.mutationLoading = false;
        state.mutationError = action.payload;
      });
  },
});

export const { clearMutationError, clearMutationSuccess, resetMutationState } = adminVehicleMakeSlice.actions;
export default adminVehicleMakeSlice.reducer;